/**
 * Location picker (5 Oct 2026): Country → State → City, each a search-as-you-type box over
 * real data (src/lib/places.ts). Picking a country clears its state and city; picking a
 * state clears the city. The city box also takes a city typed by hand ("Use 'Whitefield'"),
 * because the data has gaps for some countries.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Loader2 } from 'lucide-react'
import { T } from '@/components/shell/orgTheme'
import { focusRing, inputStyle } from '@/components/org/orgHelpers'
import type { LocationValue } from '@/lib/attributesModel'
import { citiesOf, countries, countryNamed, statesOf, usePlacesReady } from '@/lib/places'

/** `cell`: wrap each box in its own labelled field — the profile lays Country · State · City out as
 *  three grid cells (5 Oct 2026). Without it, the three sit side by side in one block. */
export function LocationInput({ value, onChange, big, cell }: { value: LocationValue | null; onChange: (v: LocationValue | null) => void; big?: boolean; cell?: (label: string, box: ReactNode) => ReactNode }) {
  const ready = usePlacesReady()
  const v = value ?? { country: '' }
  const [cities, setCities] = useState<{ key: string; list: string[] } | null>(null)
  const cityKey = v.country && v.state ? `${v.country}|${v.state}` : ''
  useEffect(() => {
    if (!cityKey) return
    let live = true
    void citiesOf(v.country, v.state!).then(list => { if (live) setCities({ key: cityKey, list }) })
    return () => { live = false }
  }, [cityKey, v.country, v.state])

  const countryItems = useMemo(() => (ready ? countries().map(c => ({ value: c.name, label: c.name, icon: c.emoji })) : []), [ready])
  const stateItems = ready ? statesOf(v.country).map(s => ({ value: s, label: s })) : []
  const cityItems = cities?.key === cityKey ? cities.list.map(c => ({ value: c, label: c })) : null

  const boxes: Array<[string, ReactNode]> = [
    ['Country', (
      <Combo big={big} label="Country" placeholder="Country" value={v.country} icon={countryNamed(v.country)?.emoji}
        items={countryItems} loading={!ready}
        onPick={c => onChange(c ? { country: c } : null)} />
    )],
    ['State', (
      <Combo big={big} label="State" placeholder="State" value={v.state ?? ''} disabled={!v.country}
        items={stateItems} loading={!ready}
        onPick={s => onChange({ country: v.country, state: s || undefined })} />
    )],
    ['City', (
      <Combo big={big} label="City" placeholder="City" value={v.city ?? ''} disabled={!v.state}
        items={cityItems ?? []} loading={!!cityKey && !cityItems} allowTyped
        onPick={c => onChange({ ...v, city: c || undefined })} />
    )],
  ]
  if (cell) return <>{boxes.map(([l, b]) => <span key={l} style={{ display: 'contents' }}>{cell(l, b)}</span>)}</>
  return (
    <span style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
      {boxes.map(([l, b]) => <span key={l} style={{ display: 'contents' }}>{b}</span>)}
    </span>
  )
}

interface Item { value: string; label: string; icon?: string }

const MAX_SHOWN = 80

/** A search-as-you-type single picker. Typing filters; Enter or a click picks. */
function Combo({ label, placeholder, value, items, onPick, disabled, loading, allowTyped, icon, big }: {
  label: string; placeholder: string; value: string; items: Item[]; onPick: (v: string) => void
  disabled?: boolean; loading?: boolean; allowTyped?: boolean; icon?: string; big?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) { setOpen(false); setQ('') } }
    document.addEventListener('mousedown', off)
    return () => document.removeEventListener('mousedown', off)
  }, [open])

  const needle = q.trim().toLowerCase()
  // Names that START with what was typed come first ("Ban" → Bangalore before Durban).
  const matches = needle
    ? [...items.filter(i => i.label.toLowerCase().startsWith(needle)), ...items.filter(i => !i.label.toLowerCase().startsWith(needle) && i.label.toLowerCase().includes(needle))]
    : items
  const exact = items.some(i => i.label.toLowerCase() === needle)
  const typed = allowTyped && needle && !exact ? q.trim() : null
  const pick = (val: string) => { onPick(val); setOpen(false); setQ('') }

  return (
    <div ref={box} style={{ position: 'relative', minWidth: 0 }}>
      <span style={{ position: 'relative', display: 'block' }}>
        {icon && !open && <span aria-hidden style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 15 }}>{icon}</span>}
        <input aria-label={label} disabled={disabled} placeholder={placeholder}
          value={open ? q : value} onChange={e => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); if (matches[0]) pick(matches[0].value); else if (typed) pick(typed) }
            if (e.key === 'Escape') { setOpen(false); setQ('') }
          }}
          className={focusRing}
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', padding: `${big ? 12 : 9}px 32px ${big ? 12 : 9}px ${icon && !open ? 34 : 12}px`, fontSize: big ? 14.5 : 14, borderRadius: 10,
            background: disabled ? T.page : '#fff', cursor: disabled ? 'not-allowed' : 'text', textOverflow: 'ellipsis' }} />
        <span aria-hidden style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'grid', color: T.muted, pointerEvents: 'none' }}>
          {loading && open ? <Loader2 size={15} className="animate-spin" /> : <ChevronDown size={15} />}
        </span>
      </span>
      {open && !disabled && (
        <div role="listbox" aria-label={label} style={{ position: 'absolute', left: 0, right: 0, minWidth: 220, top: 'calc(100% + 4px)', zIndex: 70, maxHeight: 280, overflowY: 'auto', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 10px 30px rgba(28,26,34,.12)', padding: 4 }}>
          {loading && <div style={{ padding: '10px 12px', fontSize: 13.5, color: T.faint }}>Loading…</div>}
          {!loading && value && !needle && (
            <button role="option" aria-selected={false} onClick={() => pick('')} className="hover:bg-[#F4F2F6]" style={optStyle(T.muted)}>Clear</button>
          )}
          {!loading && matches.slice(0, MAX_SHOWN).map(i => (
            <button key={i.value} role="option" aria-selected={i.value === value} onClick={() => pick(i.value)} className="hover:bg-[#F4F2F6]"
              style={{ ...optStyle(T.dark), fontWeight: i.value === value ? 600 : 400 }}>
              {i.icon && <span aria-hidden style={{ marginRight: 8 }}>{i.icon}</span>}{i.label}
            </button>
          ))}
          {!loading && matches.length > MAX_SHOWN && <div style={{ padding: '6px 12px', fontSize: 12.5, color: T.faint }}>Keep typing to narrow {matches.length.toLocaleString()} {label.toLowerCase() === 'city' ? 'cities' : 'results'}</div>}
          {!loading && typed && (
            <button role="option" aria-selected={false} onClick={() => pick(typed)} className="hover:bg-[#F4F2F6]" style={optStyle(T.purpleText)}>Use “{typed}”</button>
          )}
          {!loading && !matches.length && !typed && <div style={{ padding: '10px 12px', fontSize: 13.5, color: T.faint }}>{items.length ? 'No matches' : allowTyped ? 'Type a city' : 'Nothing to pick'}</div>}
        </div>
      )}
    </div>
  )
}

const optStyle = (color: string) => ({
  width: '100%', textAlign: 'left' as const, display: 'block', padding: '8px 12px', border: 'none', background: 'transparent',
  borderRadius: 7, cursor: 'pointer', fontSize: 14, color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
})
